import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@noor/ui';
import { cn } from '@noor/lib';
import { Can } from '@/entities/access';
import type { ModerationEntityType } from '@/entities/moderation';
import { useApproveChangeRequest } from '../model/use-approve-change-request';
import { useRejectChangeRequest } from '../model/use-reject-change-request';
import { RejectReasonDialog } from './reject-reason-dialog';

interface ModerationDecisionButtonsProps {
  entityType: ModerationEntityType;
  changeRequestId: string;
  /** The modal's "all changed fields reviewed" gate — approve stays disabled until set. */
  reviewConfirmed: boolean;
  /** Operation-specific captions (e.g. "Одобрить удаление"). */
  approveLabel?: string | undefined;
  rejectLabel?: string | undefined;
  /** Disable both actions (e.g. a sibling decision is in flight). */
  disabled?: boolean | undefined;
  onDone?: (() => void) | undefined;
  className?: string | undefined;
}

/**
 * Approve + reject pair for one change request (Figma decision row): two
 * half-width pill buttons — grey reject, dark approve. Approve fires
 * directly once the review gate is confirmed; reject opens the reason
 * dialog (the API requires a reason).
 */
export function ModerationDecisionButtons({
  entityType,
  changeRequestId,
  reviewConfirmed,
  approveLabel,
  rejectLabel,
  disabled = false,
  onDone,
  className,
}: ModerationDecisionButtonsProps) {
  const { t } = useTranslation('moderation');
  const [rejectOpen, setRejectOpen] = useState(false);
  const approve = useApproveChangeRequest(entityType);
  const reject = useRejectChangeRequest(entityType);

  const busy = disabled || approve.isPending || reject.isPending;

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-2 gap-x-[var(--form-gap-x)] gap-y-[var(--form-gap-y)]',
          className,
        )}
      >
        <Can permission={`moderation.${entityType}.reject`}>
          <Button
            type="button"
            variant="soft"
            shape="rounded"
            size="modal"
            disabled={busy}
            onClick={() => setRejectOpen(true)}
            className="w-full"
          >
            {rejectLabel ?? t('reject.decide', 'Отклонить изменения')}
          </Button>
        </Can>
        <Can permission={`moderation.${entityType}.approve`}>
          <Button
            type="button"
            variant="dark"
            shape="rounded"
            size="modal"
            disabled={busy || !reviewConfirmed}
            aria-busy={approve.isPending}
            onClick={() => {
              if (busy || !reviewConfirmed) return;
              approve.mutate({ changeRequestId }, { onSuccess: () => onDone?.() });
            }}
            className="w-full"
          >
            {approve.isPending && <Spinner size="sm" className="text-current" />}
            {approveLabel ?? t('approve.decide', 'Одобрить изменения')}
          </Button>
        </Can>
      </div>

      <RejectReasonDialog
        open={rejectOpen}
        pending={reject.isPending}
        onClose={() => setRejectOpen(false)}
        onConfirm={(reason) =>
          reject.mutate(
            { changeRequestId, reason },
            {
              onSuccess: () => {
                setRejectOpen(false);
                onDone?.();
              },
            },
          )
        }
      />
    </>
  );
}
